import React from 'react';
import { Display } from '../../components/Display/Display';
import FileSearchDisplay from '../../components/FileSearch';
import { GrtAiMarkdown } from '../../components/Markdown/GrtAiMarkdown';
import CourseRecommend from '../pageComponents/CourseRecommend';
import MultimodalLearning from '../pageComponents/MultimodalLearning';
import Review from '../Web/PageComponents/Review';
import AiCard from '../Web/PageComponents/CaseSimulation/AiCard';
import ResearchPlanCard from '../../components/ResearchPlanCard';

/**
 * 渲染 AI 消息的函数，原来内联在 useChat.js 中
 * 所需参数请传入对应的外部依赖和数据
 */
export const renderAiMessage = ({
    message,
    from,
    index,
    len,
    item,
    deerFlowHook,
    onSubmitMsg,
    skillStore,
    retrieval_result,
    web_result,
    content_type,
    processing,
    recommend_course_result,
    multimodal_learning_result,
    ai_evaluation,
    card_result,
    handleMessages,
    setDetailPanelOpen,
    setMultimodalReference,
    detailPanelOpen,
    activeDetailMessageId,
    setActiveDetailMessageId,
    changeSessionMapItem,
    getCurSessionMsgs,
    changeOriginMsgMapRef,
    originMsgMapRef,
    currentSessionMapIdRef,
    createRequestByMsgId,
    onClickCitation,
    getBaseParams,
    side,
    currentAgent
}) => {
    // 检查消息是否为React组件
    if (React.isValidElement(message)) {
        return (
            <>
                <div className="message-wrap" data-type={content_type}>
                    {message}
                </div>
            </>
        );
    }

    // 处理字符串或对象形式的消息，支持从字符串中提取研究计划 JSON，
    // 也支持 message 为对象且包含 answerContent / content 字段的情况。
    let displayMessage = message;
    let planComponent = null;
    try {
        const extractJSONFromString = (text) => {
            if (!text || typeof text !== 'string') return null;
            // 快速包含检查，避免无谓计算
            if (!(text.includes('{') && text.includes('"title"') && text.includes('"steps"') && text.includes('}'))) {
                return null;
            }
            const possibleStarts = [];
            let idx = -1;
            while ((idx = text.indexOf('{', idx + 1)) !== -1) {
                possibleStarts.push(idx);
            }

            for (const start of possibleStarts) {
                let openBraces = 1;
                for (let i = start + 1; i < text.length; i++) {
                    if (text[i] === '{') openBraces++;
                    if (text[i] === '}') openBraces--;
                    if (openBraces === 0) {
                        const jsonCandidate = text.substring(start, i + 1);
                        try {
                            const parsed = JSON.parse(jsonCandidate);
                            if (parsed && parsed.title && Array.isArray(parsed.steps)) {
                                return {
                                    json: parsed,
                                    start: start,
                                    end: i + 1
                                };
                            }
                        } catch (e) {
                            continue;
                        }
                    }
                }
            }
            return null;
        };

        // 如果 message 是对象，优先尝试从常见字段中获取字符串内容
        let candidateString = null;
        if (typeof message === 'string') {
            candidateString = message;
        } else if (message && typeof message === 'object') {
            // 优先使用 answerContent（常见字段），其次使用 content（可能是字符串）
            if (typeof message.answerContent === 'string' && message.answerContent.trim() !== '') {
                candidateString = message.answerContent;
            } else if (typeof message.content === 'string' && message.content.trim() !== '') {
                candidateString = message.content;
            } else if (message.content && typeof message.content === 'object') {
                // 有些后端会把结构化 content 放在 object 中的 html/text 字段
                candidateString = message.content.html || message.content.text || null;
            }
        }

        const result = extractJSONFromString(candidateString);
        if (result) {
            const { json: planData, start } = result;
            if (from === 'chat' && index === len - 1) {
                setTimeout(() => {
                    deerFlowHook.setEditPlanMode(false);
                }, 0);
            }

            // displayMessage 使用原始字符串（如果存在）截取 JSON 之前的部分
            displayMessage = candidateString ? candidateString.substring(0, start).trim() : '';
            const isLatestPlan = index === len - 1;
            const shouldDisable = !isLatestPlan && deerFlowHook.planButtonsDisabled;
            planComponent = (
                <ResearchPlanCard
                    skillStore={skillStore}
                    planData={planData}
                    isLatestPlan={isLatestPlan}
                    disabled={shouldDisable}
                    sessionId={item.session_id}
                    onEditPlan={() => {
                        deerFlowHook.setEditPlanMode(true);
                        if (item.session_id) {
                            deerFlowHook.lastThreadIdRef.current = item.session_id;
                        }
                    }}
                    onStartResearch={(currentAgent) => {
                        onSubmitMsg({ question: '太酷了！让我们开始吧。', value: 'start_research' });
                    }}
                />
            );
        }
    } catch (e) {
        displayMessage = message;
    }

    return (
        <>
            <div className="message-wrap" data-type={content_type}>
                <Display show={(retrieval_result && retrieval_result?.data?.length) || (web_result && web_result?.data?.length)}>
                    <div className="ai-files-wrap" data-side={side}>
                        <Display show={retrieval_result && retrieval_result?.data?.length}>
                            <FileSearchDisplay
                                side={side}
                                type="retrieval"
                                total={retrieval_result?.count}
                                needSearch={false}
                                fileList={retrieval_result?.data}
                            />
                        </Display>
                        <Display show={web_result && web_result?.data?.length}>
                            <FileSearchDisplay
                                side={side}
                                type="web_search"
                                total={web_result?.count}
                                needSearch={false}
                                fileList={web_result?.data}
                            />
                        </Display>
                    </div>
                </Display>
            </div>

            {typeof displayMessage === 'string' && displayMessage.trim() !== '' && (
                <GrtAiMarkdown
                    key={item._id || index}
                    side={side}
                    citation={{
                        side: side,
                        web_result: web_result,
                        retrieval_result: retrieval_result,
                        onClick: onClickCitation
                    }}
                    // 如果已有可显示的流式文本（displayMessage），优先展示，不再严格依赖 processing
                    isDone={!processing || Boolean(displayMessage)}
                    className="ai-markdown-box"
                    content={displayMessage}
                />
            )}
            <Display show={recommend_course_result}>
                <CourseRecommend side={side} recommend_course_result={recommend_course_result} />
            </Display>
            {/*
                支持两种来源渲染多模态卡片：
                1. 后端返回的 multimodal_learning_result（优先）
                2. 消息项中的 multimodal_reference（回退，用于只有引用/描述的情况）
            */}
            {(() => {

                const multimodalSource = multimodal_learning_result || item?.multimodal_reference || null;
                return (
                    <Display show={multimodalSource}>
                        <MultimodalLearning
                            multimodal_learning_result={
                                multimodal_learning_result
                                    ? multimodal_learning_result
                                    : // 将 multimodal_reference 适配为 multimodal_learning_result 结构
                                      {
                                          data: [],
                                          description: item?.multimodal_reference?.description || '',
                                          reference_id: item?.multimodal_reference?.reference_id || null,
                                          title: item?.multimodal_reference?.title || '多模态学习'
                                      }
                            }
                            setDetailPanelOpen={setDetailPanelOpen}
                            setMultimodalReference={setMultimodalReference}
                            detailPanelOpen={detailPanelOpen}
                            activeDetailMessageId={activeDetailMessageId}
                            setActiveDetailMessageId={setActiveDetailMessageId}
                            messageId={item._id || index}
                            agentName={currentAgent?.name || ''}
                        />
                    </Display>
                );
            })()}
            <Display show={ai_evaluation}>
                <Review
                    handleMessages={handleMessages}
                    changeSessionMapItem={changeSessionMapItem}
                    getCurSessionMsgs={getCurSessionMsgs}
                    changeOriginMsgMapRef={changeOriginMsgMapRef}
                    originMsgMapRef={originMsgMapRef}
                    currentSessionMapIdRef={currentSessionMapIdRef}
                    createRequestByMsgId={createRequestByMsgId}
                    messageInfo={item}
                    ai_evaluation={ai_evaluation}
                />
            </Display>
            <Display show={card_result}>
                <AiCard
                    getBaseParams={getBaseParams}
                    getCurSessionMsgs={getCurSessionMsgs}
                    changeOriginMsgMapRef={changeOriginMsgMapRef}
                    currentSessionMapIdRef={currentSessionMapIdRef}
                    createRequestByMsgId={createRequestByMsgId}
                    messageInfo={item}
                    card_result={card_result}
                />
            </Display>
            {planComponent}
        </>
    );
};

export default {
    renderAiMessage
};


